import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Divider, Grid, Paper, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { Security } from '../../proto/generated/security'
import websocketActions from '../../actions/websocket.actions'
import { subscriptionTags } from '../../assets/constants'
import { WebSocketContext } from '../webSocketConnection/WebSocket'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { selectOrdersHistory, selectOrdersHistoryLoaded } from '../../selectors/orders'
import { WidgetListItems } from '../../util/sidebar'
import { selectRightSidebarWidget } from '../../selectors/common'
import { selectQuickWidgetConfig } from '../../selectors/quickWidgets'
import { QuickWidgetTypes } from '../../reducer/user.reducer'
import { selectPrimaryTradingAccount } from '../../selectors/user'
import { Order, OrderStatus } from '../../proto/generated/order'
import NoTradingAccountEmptyState from '../tradingAccountStatus/NoTradingAccountEmptyState'
import GenericFilterTabs from '../genericTabs/genericFilterTabs'
import PositionsSidebarWidget from '../positionsSidebarWidget/positionsSidebarWidget'
import { selectPositions, selectPositionsLoaded } from '../../selectors/positions'
import { fetchSecuritySnapshotsBulk } from '../../services/apis/candles'
import { filterOrdersOpenTabWide } from '../../util/icebergOrders'
import OrdersPanelHeader from '../ordersWidget/OrdersPanelHeader'
import OrdersTableScrollArea from '../ordersWidget/OrdersTableScrollArea'

export enum PositionNavButtons {
    PNL = 'PNL',
    GREEKS = 'GREEKS'
}

export const navItems = [
    {
        key: PositionNavButtons.PNL,
        label: 'P/L'

    },
    {
        key: PositionNavButtons.GREEKS,
        label: 'Greeks'
    }
]

export enum OrdersNavButtons {
    OPEN = 'OPEN',
    ALL = 'ALL'
}

export enum OrdersNavButtonsDetailed {
    OPEN = 'OPEN',
    COMPLETE = 'COMPLETE',
    REJECTED = 'REJECTED',
    CANCELLED = 'CANCELLED',
    ALL = 'ALL',
}

export const positionsNavItems = [
    {
        key: OrdersNavButtons.OPEN,
        label: 'Open'
    },
    {
        key: OrdersNavButtons.ALL,
        label: 'All'
    }
]

export const ordersNavItems = [
    {
        key: OrdersNavButtonsDetailed.OPEN,
        label: 'Open'
    },
    {
        key: OrdersNavButtonsDetailed.COMPLETE,
        label: 'Complete'
    },
    {
        key: OrdersNavButtonsDetailed.REJECTED,
        label: 'Rejected'
    },
    {
        key: OrdersNavButtonsDetailed.CANCELLED,
        label: 'Cancelled'
    },
    {
        key: OrdersNavButtonsDetailed.ALL,
        label: 'All'
    },
]

export interface SelectedSqOffStrategyType {
    underlyingSecurity: Security | undefined
    strategyCode: string | undefined
    basketId: string | undefined
    strategyName: string | undefined
    index: number,
    analyzeAndAdjust?: boolean
}

const PositionsWidget = () => {
    const dispatch = useDispatch()
    const ws = useContext(WebSocketContext)
    const [tableViewType, setTableViewType] = useState<PositionNavButtons>(PositionNavButtons.PNL)
    const isDragging = useRef(false)
    const [ordersTableViewType, setOrdersTableViewType] = useState<OrdersNavButtonsDetailed>(OrdersNavButtonsDetailed.OPEN)
    const [searchText, setSearchText] = useState('')
    const [showSearch, setShowSearch] = useState(false)
    const [filterOrdersData, setFilterOrdersData] = useState<Order[]>([])
    // Positions and orders share the sidebar equally until the user drags the divider.
    const [positionsHeight, setPositionsHeight] = useState(0.5)

    const positions = useSelector(selectPositions)

    const ordersData = useSelector(selectOrdersHistory)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const positionsLoaded = useSelector(selectPositionsLoaded)
    const ordersLoaded = useSelector(selectOrdersHistoryLoaded)
    const rightSideWidget = useSelector(selectRightSidebarWidget)
    const positionWidgetConfig = useSelector(selectQuickWidgetConfig(QuickWidgetTypes.POSITION));

    useEffect(() => {
        return () => {
            const isQuickWidgetOpen = positionWidgetConfig?.enabled === true;
            const isSidebarOpen = rightSideWidget === WidgetListItems.POSITIONS;
            if (!isQuickWidgetOpen && !isSidebarOpen) {
                websocketActions.setWebsocketSubscribeList(dispatch, {
                    securitySnapshotSubscriptions: [
                        {
                            tag: subscriptionTags.POSITION_WIDGET_SUBSCRIPTION_TAG,
                            securities: []
                        }
                    ],
                    candleSubscriptions: [
                        {
                            tag: subscriptionTags.POSITION_WIDGET_SUBSCRIPTION_TAG,
                            timeBucket: undefined,
                            securities: []
                        }
                    ]
                });
            }
        };
    }, [dispatch, positionWidgetConfig?.enabled, rightSideWidget]);


    const getFilteredOrders = (orders: Order[]) => {
        if (showSearch && searchText.trim().length > 0) {
            return orders;
        }
        switch (ordersTableViewType) {
            case OrdersNavButtonsDetailed.OPEN:
                return filterOrdersOpenTabWide(orders);
            case OrdersNavButtonsDetailed.COMPLETE:
                return orders.filter(order => order.status === OrderStatus.EXECUTED);
            case OrdersNavButtonsDetailed.REJECTED:
                return orders.filter(order => (order.status === OrderStatus.REJECTED || order.status === OrderStatus.FAILED));
            case OrdersNavButtonsDetailed.CANCELLED:
                return orders.filter(order => order.status === OrderStatus.CANCELLED);
            case OrdersNavButtonsDetailed.ALL:
                return orders;
            default:
                return orders;
        }
    };

    const visibleOrders = getFilteredOrders(filterOrdersData);
    // The symbol filter resolves securities asynchronously, so a non-empty history briefly yields no rows;
    // keep the skeleton up until it has produced something (or a search is genuinely empty).
    const searching = showSearch && searchText.trim().length > 0
    const ordersReady = ordersLoaded && (!ordersData?.length || filterOrdersData.length > 0 || searching)
    
    // Loading snapshots for orders in right sidebar widget
    const securities = useMemo(()=>{
        return visibleOrders.filter(order => ![OrderStatus.REJECTED, OrderStatus.CANCELLED, OrderStatus.EXECUTED, OrderStatus.FAILED].includes(order.status)).map((order)=> order.security)
    },[visibleOrders])

    useEffect(() => {
        if (!securities || !securities.length) return;
        fetchSecuritySnapshotsBulk({ securities, dispatch });
        websocketActions.setWebsocketSubscribeList(dispatch, {
            securitySnapshotSubscriptions: [
                {
                    tag: subscriptionTags.ORDERS_WIDGET_SUBSCRIPTION_TAG,
                    securities
                }
            ]
        })
    }, [securities])

    const orderDataSecKeys = useMemo(() => {
        const data: string[] = []
        ordersData?.forEach((order) => {
            const secKey = getSecurityKey(order.security?.exchange ?? 0, order.security?.code ?? '')
            data.push(secKey)
        })
        return data
    }, [ordersData])
    useEffect(() => {
        let cancelled = false
        const run = async () => {
            if (!ordersData?.length) { setFilterOrdersData([]); return }
            const results = await Promise.all(
                ordersData.map((data, i) =>
                    getSecurityAsync(orderDataSecKeys[i]).then((security) =>
                        (security?.symbol)?.toLowerCase()?.includes(searchText.trim().toLowerCase())
                    )
                )
            )
            if (!cancelled) setFilterOrdersData(ordersData.filter((_, i) => results[i]))
        }
        run()
        return () => { cancelled = true }
    }, [orderDataSecKeys, ordersData, searchText])

    const startDragging = () => {
        isDragging.current = true;
        document.body.style.userSelect = "none";
        document.body.style.pointerEvents = "none";
        document.body.style.cursor = "ns-resize";
    };

    const stopDragging = () => {
        isDragging.current = false;
        document.body.style.userSelect = "auto";
        document.body.style.pointerEvents = "auto";
        document.body.style.cursor = "auto";
    };

    const onDrag = (e: MouseEvent) => {
        if (!isDragging.current) return;
        document.body.style.cursor = "ns-resize";
        const containerElement = document.querySelector('[data-positions-container]') as HTMLElement;
        if (containerElement) {
            const rect = containerElement.getBoundingClientRect();
            const relativeY = e.clientY - rect.top;
            const percentage = relativeY / rect.height;
            const clampedPercentage = Math.max(0.20, Math.min(0.80, percentage));
            setPositionsHeight(clampedPercentage);
        }
    };

    useEffect(() => {
        window.addEventListener("mousemove", onDrag);
        window.addEventListener("mouseup", stopDragging);
        return () => {
            window.removeEventListener("mousemove", onDrag);
            window.removeEventListener("mouseup", stopDragging);
        };
    }, [])

    return (
        <Paper
            sx={{
                borderTopRightRadius: '6px',
                display: 'flex',
                height: '100%',
                flexDirection: 'column'
            }}
        >
            {
                primaryTradingAccount ? (
                    <>
                        <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column' }} data-positions-container>
                            <Box sx={{
                                flex: positionsHeight,
                                minHeight: '200px',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden'
                            }}>
                                <PositionsSidebarWidget positions={positions} loaded={positionsLoaded} />
                            </Box>
                            <Box
                                className="divider"
                                sx={{
                                    height: 'var(--extra-small-spacing)',
                                    cursor: 'ns-resize',
                                    backgroundColor: (theme) => theme.palette.background.default,
                                }}
                                onMouseDown={startDragging}
                            />
                            <Box sx={{ flex: 1 - positionsHeight, overflow: 'auto', display: 'flex', flexDirection: 'column', minHeight: '200px' }}>
                                <OrdersPanelHeader
                                    showSync={false}
                                    ordersTableViewType={ordersTableViewType}
                                    onOrdersTableViewTypeChange={setOrdersTableViewType}
                                    hasOrders={!!(ordersData && ordersData.length > 0)}
                                    showSearch={showSearch}
                                    onShowSearch={setShowSearch}
                                    searchText={searchText}
                                    onSearchTextChange={setSearchText}
                                />
                                <OrdersTableScrollArea
                                    orders={visibleOrders}
                                    tabType={ordersTableViewType}
                                    emptyText={
                                        searchText.trim().length > 0
                                            ? 'No orders match your search.'
                                            : 'Your placed and executed orders will appear here.'
                                    }
                                    showEmptyPreview={searchText.trim().length === 0}
                                    loaded={ordersReady}
                                    variant="sidebar"
                                />
                            </Box>
                        </Box>
                    </>) : (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <NoTradingAccountEmptyState subject="positions" preview="positions" />
                    </Box>
                )
            }
        </Paper>
    )
}
export default memo(PositionsWidget);
